import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import {
  CYLINDO_OPTIONS,
  CYLINDO_ACCOUNT_ID,
  FABRIC_PRODUCT_OPTION_ID,
  LEATHER_PRODUCT_OPTION_ID,
  PANTONE_FABRIC_CONFIG,
} from 'commons/constants';
import { orientationOptionIds } from 'components/consumer/OrientationPDPModal';
import { logError } from 'commons/logger';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useCylindoDimensionShots from '../useCylindoDimensionShots';
import { probeLatestVersion } from '../useCylindoDimensionShots/probeLatestVersion';

const DISABLED_UPHOLSTERY = [];

const sunbrellaNameMismatchOptions = {
  'Sunbrella Premier Fog': 'Premier Fog',
  'Sunbrella Premier Indigo': 'Premier Indigo',
  'Sunbrella Premier Wisteria': 'Premier Wisteria',
  'Sunbrella Premier Lagoon': 'Premier Lagoon',
  'Sunbrella Premier Sea': 'Premier Sea',
};

const CYLINDO_FRAMES_TO_SHOW = [1, 5, 9, 13, 17];
const CYLINDO_CONTENT_API = 'https://content.cylindo.com/api/v2';

const cylindoConfigPromises = {};

const fetchCylindoConfig = async productId => {
  if (!cylindoConfigPromises[productId]) {
    cylindoConfigPromises[productId] = axios.get(
      `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/configuration`
    );
  }
  return cylindoConfigPromises[productId];
};

const usePDPCylindoViewer = ({
  isPDPDesktop = false,
  material = null,
} = {}) => {
  const [loading, setLoading] = useState(false);
  const [cylindoProductConfig, setCylindoProductConfig] = useState(null);
  const [cylindoVersion, setCylindoVersion] = useState(null);

  const { data: basicDetails } = usePDPBasicDetails();

  const {
    data: { selectedOptionsExtended },
  } = usePDPOptions();

  const productId = basicDetails?.id;
  const isCylindoProduct = !!basicDetails?.custom_properties?.cylindo_product;

  useEffect(() => {
    if (productId && isCylindoProduct) {
      setCylindoProductConfig(null);
      setCylindoVersion(null);
      setLoading(true);

      fetchCylindoConfig(productId)
        .then(res => {
          if (!res.data) {
            return null;
          }
          setCylindoProductConfig(res.data);
          return probeLatestVersion(productId, res.data.version).then(
            setCylindoVersion
          );
        })
        .catch(e => {
          logError(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // cleanup
    return () => {
      setCylindoProductConfig(null);
      setCylindoVersion(null);
    };
  }, [productId, isCylindoProduct]);

  const nameMismatchOptions = useMemo(
    () => ({
      ...sunbrellaNameMismatchOptions,
      ...PANTONE_FABRIC_CONFIG.pantoneFabricCylindoNameMapping,
    }),
    []
  );

  // upholstery - rename to upholsteryKey
  const upholsteryKey = useMemo(() => {
    if (!productId || !cylindoProductConfig || !selectedOptionsExtended) {
      return null;
    }

    const upholsteryName =
      material?.label ??
      selectedOptionsExtended[FABRIC_PRODUCT_OPTION_ID]?.value?.value ??
      selectedOptionsExtended[LEATHER_PRODUCT_OPTION_ID]?.value?.value ??
      null;

    if (!upholsteryName) {
      return null;
    }

    // fix for (Sunbrella) Premier Indigo, Premier Fog, Premier Wisteria,
    // Premier Lagoon, Premier Sea, e.g. a mismatch of "Sunbrella Premier
    // Indigo" and "Premier Indigo"
    // Also replace Pantone fabric names
    const correctedUpholsteryName =
      nameMismatchOptions[upholsteryName] || upholsteryName;

    return correctedUpholsteryName
      .trim()
      .replace('™', '')
      .toUpperCase();
  }, [
    cylindoProductConfig,
    material?.label,
    nameMismatchOptions,
    productId,
    selectedOptionsExtended,
  ]);

  const hasUpholsteryOption = !!upholsteryKey;

  const dimensionShotsData = useCylindoDimensionShots({
    upholsteryKey,
    cylindoVersion,
  });

  const upholsteryFeatureDetails = useMemo(() => {
    // Find selected upholstery in config
    if (!upholsteryKey || DISABLED_UPHOLSTERY.includes(upholsteryKey)) {
      return undefined;
    }

    const upholsteryFeature = cylindoProductConfig.features?.find(
      feat => feat.code === CYLINDO_OPTIONS.UPHOLSTERY
    );

    const upholsteryOption = upholsteryFeature?.options.find(
      opt => opt.code?.toUpperCase() === upholsteryKey
    );

    if (!upholsteryOption) {
      return undefined;
    }

    return {
      [CYLINDO_OPTIONS.UPHOLSTERY]: upholsteryOption.code,
    };
  }, [cylindoProductConfig, upholsteryKey]);

  const isUpholsteryFeatureAvailable = !!upholsteryFeatureDetails;

  const orientationKey = useMemo(() => {
    const orientationId = orientationOptionIds.find(
      id => selectedOptionsExtended[id]
    );

    const orientationValue =
      selectedOptionsExtended[orientationId]?.value?.value;

    return orientationValue?.toUpperCase();
  }, [selectedOptionsExtended]);

  const hasOrientationOption = !!orientationKey;

  const orientationOptionDetails = useMemo(() => {
    // Find selected orientation in config
    if (!orientationKey) {
      return undefined;
    }

    const orientationFeature = cylindoProductConfig?.features?.find(
      feat => feat.code === CYLINDO_OPTIONS.ORIENTATION
    );
    const orientationKeyStr = String(orientationKey)
      .trim()
      .toLowerCase();
    const orientationOption = orientationFeature?.options?.find(
      opt =>
        String(opt.code)
          .trim()
          .toLowerCase() === orientationKeyStr
    );
    if (!orientationOption) {
      return undefined;
    }

    return {
      [CYLINDO_OPTIONS.ORIENTATION]: orientationOption.code,
    };
  }, [cylindoProductConfig?.features, orientationKey]);

  const isOrientationFeatureAvailable = !!orientationOptionDetails;

  const areAllFeaturesAvailable =
    (!hasUpholsteryOption ||
      (hasUpholsteryOption && isUpholsteryFeatureAvailable)) &&
    (!hasOrientationOption ||
      (hasOrientationOption && isOrientationFeatureAvailable));

  const cylindoFeatures = useMemo(
    () => ({
      ...(upholsteryFeatureDetails || {}),
      ...(orientationOptionDetails || {}),
    }),
    [orientationOptionDetails, upholsteryFeatureDetails]
  );

  const cylindoConfigurationPhotos = useMemo(() => {
    if (
      isPDPDesktop &&
      areAllFeaturesAvailable &&
      Object.entries(cylindoFeatures).length
    ) {
      const featuresString = Object.entries(cylindoFeatures)
        .map(([featureKey, featureCode]) => `${featureKey}:${featureCode}`)
        .join('&feature=');

      return CYLINDO_FRAMES_TO_SHOW.map((frame, index) => ({
        id: frame,
        index,
        src: `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/frames/${frame}?feature=${featuresString}`,
        type: 'cylindo',
      }));
    }

    return [];
  }, [areAllFeaturesAvailable, cylindoFeatures, isPDPDesktop, productId]);

  const returnValue = {
    cylindoConfigurationPhotos,
    cylindoFeatures,
    isCylindoAvailable: !!cylindoProductConfig && areAllFeaturesAvailable,
    loading,
    isCylindoConfigurationPhotos:
      isCylindoProduct &&
      !!cylindoProductConfig &&
      areAllFeaturesAvailable &&
      cylindoConfigurationPhotos?.length > 0,
    upholsteryKey,
    ...dimensionShotsData,
  };

  return returnValue;
};

export default usePDPCylindoViewer;
